import React, { useEffect, useState } from 'react'
import { Stack } from '@mui/material'
import TemplateCard from '../../../components/TemplateCard'
import ResourceNotFound from '../../../components/ResourceNotFound'
import Wrapper from '../../../components/Wrapper'
import { makeStyles, Tab, Tabs, Typography } from '@material-ui/core'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { fetchConfig, updateConfigById } from '../../../store/config'
import { Roles } from '../../../constants/index'

const TemplateNames = {
  exhibitorInvitationEmail: { id: 'exhibitorInvitationEmail', name: 'Exhibitor Invitation Email' },
  exhibitorReminderEmail: { id: 'exhibitorReminderEmail', name: 'Exhibitor Reminder Email' },
  exhibitorConfirmationEmail: {
    id: 'exhibitorConfirmationEmail',
    name: 'Exhibitor Confirmation Email'
  },
  exhibitorUpdateInfoEmail: {
    id: 'exhibitorUpdateInfoEmail',
    name: 'Exhibitor Data Imported Email'
  },
  speakerInvitationEmail: { id: 'speakerInvitationEmail', name: 'Speaker Invitation Email' },
  speakerReminderEmail: { id: 'speakerReminderEmail', name: 'Speaker Reminder Email' },
  speakerConfirmationEmail: { id: 'speakerConfirmationEmail', name: 'Speaker Confirmation Email' },
  speakerReminderEmailTalkSubmissions: {
    id: 'speakerReminderEmailTalkSubmissions',
    name: 'Speaker Reminder Email for Talk Submissions'
  },
  speakerUpdateInfoEmail: {
    id: 'speakerUpdateInfoEmail',
    name: 'Speaker Data Imported Email'
  },

  sponsorInvitationEmail: { id: 'sponsorInvitationEmail', name: 'Sponsor Invitation Email' },
  sponsorReminderEmail: { id: 'sponsorReminderEmail', name: 'Sponsor Reminder Email' },
  sponsorConfirmationEmail: { id: 'sponsorConfirmationEmail', name: 'Sponsor Confirmation Email' },
  sponsorUpdateInfoEmail: {
    id: 'sponsorUpdateInfoEmail',
    name: 'Sponsor Data Imported Email'
  },

  exportLeadsEmail: { id: 'exportLeadsEmail', name: 'Export Leads Email' },
  attendeeInvitationEmail: { id: 'attendeeInvitationEmail', name: 'Attendee Invitation Email' },
  attendeeReminderEmail: { id: 'attendeeReminderEmail', name: 'Attendee Reminder Email' }
}

const templateTypes = {
  exhibitor: {
    id: 'exhibitor',
    label: 'Exhibitor',
    items: [
      TemplateNames.exhibitorInvitationEmail,
      TemplateNames.exhibitorReminderEmail,
      TemplateNames.exhibitorConfirmationEmail,
      TemplateNames.exhibitorUpdateInfoEmail
    ]
  },
  speaker: {
    id: 'speaker',
    label: 'Speaker',
    items: [
      TemplateNames.speakerInvitationEmail,
      TemplateNames.speakerReminderEmail,
      TemplateNames.speakerConfirmationEmail,
      TemplateNames.speakerUpdateInfoEmail,
      TemplateNames.speakerReminderEmailTalkSubmissions
    ]
  },
  sponsor: {
    id: 'sponsor',
    label: 'Sponsor',
    items: [
      TemplateNames.sponsorInvitationEmail,
      TemplateNames.sponsorReminderEmail,
      TemplateNames.sponsorConfirmationEmail,
      TemplateNames.sponsorUpdateInfoEmail
    ]
  },
  attendee: {
    id: 'attendee',
    label: 'Attendee',
    items: [
      TemplateNames.attendeeInvitationEmail,
      TemplateNames.attendeeReminderEmail,
      TemplateNames.exportLeadsEmail
    ]
  }
}

const DefaultEmailTemplates = () => {
  const classes = useStyles()
  const [value, setValue] = useState(templateTypes.exhibitor.id)
  const [isLoading, setIsLoading] = useState(false)
  const { user } = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const { config, inProgress } = useSelector((state) => state.config)

  useEffect(() => {
    dispatch(fetchConfig())
  }, [dispatch])

  const onSaveTemplate = async (newValues, template, closeModal) => {
    if (user?.role === Roles.SUPER_ADMIN) {
      startLoading()
      await dispatch(
        updateConfigById(
          config?.id,
          {
            emailTemplates: {
              ...config?.emailTemplates,
              [template?.id]: {
                subject: newValues?.subject?.trim(),
                body: newValues?.body?.trim()
              }
            }
          },
          () => {
            toast.success('Email template saved successfully.')
            stopLoading()
          },
          () => {
            toast.error('Failed to save the template. Please try again.')
            stopLoading()
          }
        )
      )
      closeModal()
    }
  }

  const startLoading = () => setIsLoading(true)

  const stopLoading = () => setIsLoading(false)

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  return (
    <div>
      <Typography variant="h4">Email Templates</Typography>
      <Tabs
        value={value}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="off"
        className={classes.tabs}>
        {Object.keys(templateTypes).map((key) => (
          <Tab
            label={templateTypes[key]?.label}
            key={templateTypes[key]?.id}
            value={templateTypes[key]?.id}
          />
        ))}
      </Tabs>
      <Wrapper inProgress={inProgress}>
        {value && templateTypes?.[value] ? (
          <Stack gap={2} mt={2} className={classes.cards}>
            {templateTypes[value].items
              .map((item) => ({
                name: item?.name,
                subject: config?.emailTemplates?.[item?.id]?.subject || '',
                body: config?.emailTemplates?.[item?.id]?.body || '',
                id: item?.id
              }))
              .map((item) => (
                <TemplateCard
                  key={item.id}
                  id={item.id}
                  template={item}
                  isLoading={isLoading}
                  onSaveTemplate={(values, closeModal) => onSaveTemplate(values, item, closeModal)}
                />
              ))}
          </Stack>
        ) : (
          <ResourceNotFound message="No email templates available" />
        )}
      </Wrapper>
    </div>
  )
}

export default DefaultEmailTemplates

const useStyles = makeStyles((theme) => ({
  headBar: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '1rem',
    zIndex: 999,
    position: 'sticky',
    top: '98px',
    background: theme.palette.background.paper
  },
  subText: {
    marginTop: '2px',
    color: '#979797',
    lineHeight: '10px'
  },
  tabs: {
    marginTop: '2rem',
    '& .MuiTab-root.MuiTab-labelIcon': {
      minHeight: '35px'
    },
    '& .MuiTab-root svg': {
      height: '20px',
      width: '20px',
      marginBottom: '0px !important'
    },
    '& .MuiTab-root .MuiTab-wrapper': {
      display: 'flex',
      flexDirection: 'row',
      gap: '8px',
      alignItems: 'center'
    },
    '& .MuiTab-root.Mui-selected': {
      color: theme.palette.primary.main
    }
  },
  card: {
    padding: '1rem',
    border: '1px solid #E0E0E0',
    borderRadius: '8px'
  },
  label: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#4A4A4A',
    letterSpacing: '0.5px',
    marginRight: '1rem'
  },
  header: {
    background: '#CAEDF5',
    padding: '12px',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    marginBottom: '1rem'
  },
  title: {
    color: 'rgb(66, 66, 66)',
    fontWeight: 600,
    fontSize: '16px',
    letterSpacing: '0.8px'
  },
  attendanceModule: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    border: '1px solid #E0E0E0',
    borderRadius: '8px',
    padding: '16px'
  },
  headingLabel: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '16px',
    letterSpacing: '0.8px'
  },
  timePicker: {
    width: '100%'
  }
}))
